import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { ErrorFallback } from "~/components/ErrorFallback"
import { useSetupAuthSession } from "~/services/oauth"
import { queryKeys } from "~/services/query-keys.ts"
import { isRootRelativePath, SESSION_STORAGE_KEYS, SessionStorage } from "~/utils"

// This page handles authorization code provided by Single Sign On service and setups auth session using the authorization code
export const SignIn = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const code = searchParams.get("code")

  const {
    data: setupSessionResponse,
    isLoading,
    error,
  } = useSetupAuthSession({
    code,
  })

  // Retrieve the stored redirect URL from session storage
  const rawRedirectUrl = SessionStorage.getItem(SESSION_STORAGE_KEYS.AFTER_AUTH_REDIRECT_URL) ?? ""
  // Accept only relative paths — rejects absolute URLs, protocol-relative (//), and javascript: schemes
  const isValidRedirectUrl = isRootRelativePath(rawRedirectUrl || "")
  const redirectUrl = isValidRedirectUrl ? rawRedirectUrl : ""

  useEffect(() => {
    if (setupSessionResponse) {
      // Use response to avoid extra GET /session request
      queryClient.setQueryData(queryKeys.authSession(), setupSessionResponse)

      /* The auth session is set up, redirect them to the previously stored path.
         Replace the sign-up route with redirectUrl to remove it from history as it is not supposed to be open again */
      navigate(redirectUrl, { replace: true })
    }
  }, [setupSessionResponse, redirectUrl, queryClient, navigate])

  // Redirect URL is mandatory as users cannot stay on this page and all the other pages are dynamic (depend on entity ID)
  if (!rawRedirectUrl) {
    console.error("Target page is missing. No redirects are possible.")
    return <ErrorFallback className="h-screen" />
  }

  if (!isValidRedirectUrl) {
    console.error(`Redirect URL failed validation and was rejected: "${rawRedirectUrl}"`)
    return <ErrorFallback className="h-screen" />
  }

  const isLoadingFailed = !isLoading && !setupSessionResponse
  if (error || isLoadingFailed) {
    console.error(
      `Cannot setup auth session, with provided authorization code: ${code}`,
      `Token request error:`,
      error,
      `Token request response:`,
      setupSessionResponse
    )
    return <ErrorFallback className="h-screen" />
  }

  return null
}
